import { spawn } from "node:child_process";
import { createHash } from "node:crypto";
import { accessSync, constants, readFileSync } from "node:fs";
import { access, open, realpath } from "node:fs/promises";
import { homedir, tmpdir } from "node:os";
import { delimiter, dirname, extname, isAbsolute, join } from "node:path";
import { CliDriverError, runCliDriver } from "./drivers/cli-driver.js";
const ENV_NODE_SHEBANG = "#!/usr/bin/env node";
const WINDOWS_PATHEXT = [".com", ".exe", ".bat", ".cmd"];
const SHEBANG_ARGS_SPLIT_RE = /\s+/;
const FIRST_LINE_SPLIT_RE = /\r?\n/u;
const LOGIN_SHELL_OUTPUT_LINE_SPLIT_RE = /\r?\n/u;
const SAFE_POSIX_SHELL_TOKEN_RE = /^[A-Za-z0-9_./:@%+=,-]+$/u;
const NVM_VERSION_VALUE_RE = /^v?\d+(?:\.\d+){0,2}$/u;
const NODE_VERSION_PATH_RE = /(?:^|\/)v?(\d+\.\d+\.\d+)(?:\/|$)/u;
const MACOS_TEMP_FOLDER_RE = /^\/var\/folders\/[^/]+\/[^/]+\/t(?:\/|$)/u;
const WRAPPER_PATH_SEGMENT_RE = /(?:^|[-_.])(?:shim|shims|wrapper|wrappers)(?:$|[-_.])/u;
const MAX_LAUNCH_SCRIPT_INSPECTION_BYTES = 64 * 1024;
const DEFAULT_LOGIN_SHELL_LOOKUP_TIMEOUT_MS = 1500;
const HOME_DIR = normalizeOptionalText(homedir());
const TEMP_DIR = normalizeOptionalText(tmpdir());
const loginShellExecutablePathsCache = new Map();
export function buildExecutableLaunchContract(input) {
    return {
        kind: "executable",
        commandPath: input.commandPath,
        ...(input.argsPrefix && input.argsPrefix.length > 0
            ? { argsPrefix: [...input.argsPrefix] }
            : {}),
    };
}
export function materializeCliLaunchCommand(input) {
    if (input.launchContract.kind === "executable") {
        const shell = commandPathRequiresShell(input.launchContract.commandPath);
        return {
            argsPrefix: [...(input.launchContract.argsPrefix ?? [])],
            bin: input.launchContract.commandPath,
            ...(shell ? { shell: true } : {}),
        };
    }
    return {
        argsPrefix: [
            input.launchContract.scriptPath,
            ...(input.launchContract.argsPrefix ?? []),
        ],
        bin: input.launchContract.nodePath,
    };
}
function resolveLaunchContractEntrypointPath(launchContract) {
    return launchContract.kind === "executable"
        ? launchContract.commandPath
        : launchContract.scriptPath;
}
export function buildCliLaunchInvocation(input) {
    const launchCommand = materializeCliLaunchCommand({
        launchContract: input.launchContract,
    });
    return {
        args: [...launchCommand.argsPrefix, ...input.args],
        bin: launchCommand.bin,
        ...(launchCommand.shell ? { shell: true } : {}),
    };
}
export async function verifyLaunchContractFiles(input) {
    try {
        const launchCommand = materializeCliLaunchCommand(input);
        if (input.launchContract.kind === "executable") {
            await assertExecutablePath(input.launchContract.commandPath);
        }
        else {
            await assertExecutablePath(input.launchContract.nodePath);
            await assertReadablePath(input.launchContract.scriptPath);
        }
        return {
            launchCommand,
            ok: true,
        };
    }
    catch (error) {
        return {
            errorMessage: toErrorMessage(error),
            failureKind: "completed_negative",
            ok: false,
        };
    }
}
export async function verifyCliLaunchContract(input) {
    const fileCheck = await verifyLaunchContractFiles({
        launchContract: input.launchContract,
    });
    if (!fileCheck.ok) {
        return {
            errorMessage: `${input.displayName} launch contract is broken on this device: ${fileCheck.errorMessage}`,
            failureKind: "completed_negative",
            ok: false,
        };
    }
    try {
        const invocation = buildCliLaunchInvocation({
            args: input.probeArgs,
            launchContract: input.launchContract,
        });
        await runCliDriver({
            allowNonZeroExit: false,
            args: invocation.args,
            bin: invocation.bin,
            cwd: input.cwd,
            env: input.env,
            timeoutMs: input.timeoutMs ?? 15_000,
        });
        return fileCheck;
    }
    catch (error) {
        return {
            errorMessage: `${input.displayName} can't start on this device: ${toErrorMessage(error)}`,
            failureKind: classifyLaunchContractVerificationFailure(error),
            ok: false,
        };
    }
}
export async function resolveLaunchCandidatesAfterVerification(input) {
    const outcomes = await Promise.all(input.candidates.map(async (candidate) => ({
        candidate,
        verification: await input.verify(candidate),
    })));
    const verifiedCandidates = outcomes
        .filter(({ verification }) => verification.ok)
        .map(({ candidate }) => candidate);
    if (verifiedCandidates.length > 0) {
        return verifiedCandidates;
    }
    return outcomes
        .filter(({ verification }) => !verification.ok && verification.failureKind === "incomplete_check")
        .map(({ candidate }) => candidate);
}
function classifyLaunchContractVerificationFailure(error) {
    return error instanceof CliDriverError &&
        error.code === "process.non_zero_exit" &&
        typeof error.exitCode === "number"
        ? "completed_negative"
        : "incomplete_check";
}
export async function resolveInstalledNodeScriptLaunchContract(input) {
    const candidates = await resolveInstalledNodeScriptLaunchCandidates({
        additionalCommandDirectories: input.additionalCommandDirectories ?? null,
        commandName: input.commandName,
        configuredCommand: input.configuredCommand ?? null,
        displayName: input.displayName,
    });
    if (!candidates.ok) {
        return candidates;
    }
    const candidate = candidates.candidates[0] ?? null;
    if (!candidate) {
        return {
            errorMessage: `${input.displayName} is not installed on this device.`,
            ok: false,
        };
    }
    return {
        commandPath: candidate.commandPath,
        launchContract: candidate.launchContract,
        ok: true,
    };
}
export async function resolveInstalledNodeScriptLaunchCandidates(input) {
    const preferredCommandPath = input.preferredLaunchContract
        ? resolveLaunchContractEntrypointPath(input.preferredLaunchContract)
        : null;
    const commandPaths = await resolveInstalledCommandPathsForLaunch({
        additionalCommandDirectories: input.additionalCommandDirectories ?? null,
        commandName: input.commandName,
        configuredCommand: input.configuredCommand ?? null,
        preferredCommandPath,
    });
    if (commandPaths.length === 0) {
        return {
            errorMessage: `${input.displayName} is not installed on this device.`,
            reason: "not_installed",
            ok: false,
        };
    }
    const candidates = [];
    const candidateIds = new Set();
    const inspectionErrors = [];
    for (const commandPath of commandPaths) {
        const candidate = await inspectInstalledNodeScriptLaunchCandidate({
            commandPath,
            displayName: input.displayName,
        });
        if (!candidate.ok) {
            inspectionErrors.push(candidate.errorMessage);
            continue;
        }
        if (candidateIds.has(candidate.dedupeId)) {
            continue;
        }
        candidateIds.add(candidate.dedupeId);
        candidates.push({
            ...candidate,
            preferred: preferredCommandPath !== null &&
                normalizePathForDetection(commandPath) ===
                    normalizePathForDetection(preferredCommandPath),
        });
    }
    if (candidates.length === 0) {
        return {
            errorMessage: inspectionErrors[0] ??
                `${input.displayName} is installed, but OpenMeld could not inspect its launch files.`,
            reason: "not_launchable",
            ok: false,
        };
    }
    const stableCandidates = candidates.filter((candidate) => !candidate.transientWrapper);
    // Ignore incidental temporary wrappers once a stable install exists, but
    // never hide the wrapper this computer is already configured to use.
    const selectedCandidates = stableCandidates.length > 0
        ? candidates.filter((candidate) => !candidate.transientWrapper || candidate.preferred)
        : candidates;
    return {
        candidates: finalizeLaunchCandidateDisplayMetadata(selectedCandidates.map((candidate) => candidate.launchCandidate)),
        ok: true,
    };
}
async function inspectInstalledNodeScriptLaunchCandidate(input) {
    try {
        await assertExecutablePath(input.commandPath);
        const resolvedScriptPath = await realpath(input.commandPath);
        const script = await readLaunchScriptInspection(resolvedScriptPath);
        const nodePath = await resolvePinnedNodePath({
            firstLine: script.firstLine,
            commandPath: input.commandPath,
            scriptPath: resolvedScriptPath,
        });
        const transientWrapper = isTransientPathWrapper({
            commandPath: input.commandPath,
            contents: script.contents,
            firstLine: script.firstLine,
            scriptPath: resolvedScriptPath,
        });
        const launchContract = nodePath
            ? {
                kind: "node_script",
                nodePath,
                // Keep the user-facing command entrypoint in the launch contract.
                // Managed CLIs such as cursor-agent update this symlink in place;
                // persisting its resolved version file would pin OpenMeld to the old
                // release forever even though the official command has moved on.
                scriptPath: input.commandPath,
            }
            : buildExecutableLaunchContract({
                commandPath: input.commandPath,
            });
        const canonicalLaunchContract = nodePath
            ? {
                kind: "node_script",
                nodePath,
                scriptPath: resolvedScriptPath,
            }
            : buildExecutableLaunchContract({
                commandPath: resolvedScriptPath,
            });
        return {
            dedupeId: buildLaunchCandidateInstallId(canonicalLaunchContract),
            launchCandidate: buildLaunchCandidate({
                commandPath: input.commandPath,
                launchContract,
                transientWrapper,
            }),
            ok: true,
            transientWrapper,
        };
    }
    catch (error) {
        return {
            errorMessage: `${input.displayName} is installed, but OpenMeld could not inspect its launch files: ${toErrorMessage(error)}`,
            ok: false,
        };
    }
}
export function resolveInstalledCommandPath(input) {
    return resolveInstalledCommandPaths(input)[0] ?? null;
}
export function resolveInstalledCommandPaths(input) {
    const configuredCommand = normalizeOptionalText(input.configuredCommand);
    if (configuredCommand) {
        if (isAbsolute(configuredCommand)) {
            return [configuredCommand];
        }
        if (configuredCommand.includes("/") || configuredCommand.includes("\\")) {
            return [];
        }
        return resolveExecutablePathsOnPath(configuredCommand);
    }
    return resolveExecutablePathsOnPath(input.commandName);
}
async function resolveInstalledCommandPathsForLaunch(input) {
    const preferredCommandPath = normalizeOptionalText(input.preferredCommandPath);
    // A saved install may no longer be reachable through this Service process's
    // PATH. Treat it as one more discovery seed, never as trusted inventory:
    // every returned path is still inspected here and launch-verified by the
    // owning adapter.
    const preferredCommandPaths = preferredCommandPath && isAbsolute(preferredCommandPath)
        ? [preferredCommandPath]
        : [];
    const configuredCommand = normalizeOptionalText(input.configuredCommand);
    if (configuredCommand) {
        if (isAbsolute(configuredCommand)) {
            return mergeUniquePaths(preferredCommandPaths, [configuredCommand]);
        }
        if (configuredCommand.includes("/") || configuredCommand.includes("\\")) {
            return preferredCommandPaths;
        }
        return mergeUniquePaths(preferredCommandPaths, await resolveExecutablePathsFromSearchSurface({
            additionalCommandDirectories: input.additionalCommandDirectories ?? null,
            commandName: configuredCommand,
        }));
    }
    return mergeUniquePaths(preferredCommandPaths, await resolveExecutablePathsFromSearchSurface({
        additionalCommandDirectories: input.additionalCommandDirectories ?? null,
        commandName: input.commandName,
    }));
}
export function resolveExecutableOnPath(commandName) {
    return resolveExecutablePathsOnPath(commandName)[0] ?? null;
}
export function resolveExecutablePathsOnPath(commandName) {
    const normalizedCommandName = normalizeOptionalText(commandName);
    if (!normalizedCommandName) {
        return [];
    }
    const resolvedPaths = [];
    for (const directory of resolvePathDirectories()) {
        for (const candidateName of resolveCommandFileNameCandidates(normalizedCommandName)) {
            const candidate = join(directory, candidateName);
            if (pathLooksExecutable(candidate) &&
                !resolvedPaths.includes(candidate)) {
                resolvedPaths.push(candidate);
            }
        }
    }
    return resolvedPaths;
}
async function resolveExecutablePathsFromSearchSurface(input) {
    const pathMatches = resolveExecutablePathsOnPath(input.commandName);
    const additionalDirectoryMatches = resolveExecutablePathsFromDirectories({
        commandName: input.commandName,
        directories: input.additionalCommandDirectories ?? [],
    });
    const knownInstallMatches = resolveExecutablePathsFromKnownInstallDirectories(input.commandName);
    const localMatches = mergeUniquePaths(mergeUniquePaths(pathMatches, additionalDirectoryMatches), knownInstallMatches);
    if (localMatches.length > 0) {
        return localMatches;
    }
    const loginShellPaths = await resolveExecutablePathsFromLoginShell(input.commandName);
    return mergeUniquePaths(localMatches, loginShellPaths);
}
function resolvePathDirectories() {
    const directories = [];
    const pathValue = normalizeOptionalText(process.env.PATH);
    for (const pathEntry of pathValue?.split(delimiter) ?? []) {
        addUniqueDirectory(directories, pathEntry);
    }
    return directories;
}
function resolveExecutablePathsFromKnownInstallDirectories(commandName) {
    return resolveExecutablePathsFromDirectories({
        commandName,
        directories: resolveKnownInstallDirectories(),
    });
}
function resolveExecutablePathsFromDirectories(input) {
    const normalizedCommandName = normalizeOptionalText(input.commandName);
    if (!normalizedCommandName) {
        return [];
    }
    const resolvedPaths = [];
    for (const directory of input.directories) {
        for (const candidateName of resolveCommandFileNameCandidates(normalizedCommandName)) {
            const candidate = join(directory, candidateName);
            if (pathLooksExecutable(candidate) &&
                !resolvedPaths.includes(candidate)) {
                resolvedPaths.push(candidate);
            }
        }
    }
    return resolvedPaths;
}
function resolveKnownInstallDirectories() {
    const directories = [];
    if (process.platform === "win32") {
        return directories;
    }
    const home = resolveHomeDirectory();
    const nvmDir = normalizeOptionalText(process.env.NVM_DIR) ?? join(home, ".nvm");
    const pnpmHome = normalizeOptionalText(process.env.PNPM_HOME) ??
        (process.platform === "darwin"
            ? join(home, "Library", "pnpm")
            : join(home, ".local", "share", "pnpm"));
    const bunHome = normalizeOptionalText(process.env.BUN_INSTALL) ?? join(home, ".bun");
    const voltaHome = normalizeOptionalText(process.env.VOLTA_HOME) ?? join(home, ".volta");
    const asdfHome = normalizeOptionalText(process.env.ASDF_DIR) ?? join(home, ".asdf");
    const fnmHome = normalizeOptionalText(process.env.FNM_DIR) ?? join(home, ".fnm");
    addUniqueDirectory(directories, join(home, ".local", "bin"));
    addUniqueDirectory(directories, "/usr/local/bin");
    addUniqueDirectory(directories, "/opt/homebrew/bin");
    addUniqueDirectory(directories, join(bunHome, "bin"));
    addUniqueDirectory(directories, pnpmHome);
    addUniqueDirectory(directories, join(home, ".npm-global", "bin"));
    addUniqueDirectory(directories, join(home, ".yarn", "bin"));
    addUniqueDirectory(directories, join(home, ".config", "yarn", "global", "node_modules", ".bin"));
    addUniqueDirectory(directories, join(voltaHome, "bin"));
    addUniqueDirectory(directories, join(asdfHome, "shims"));
    addUniqueDirectory(directories, join(fnmHome, "aliases", "default", "bin"));
    addUniqueDirectory(directories, join(home, ".local", "share", "fnm", "aliases", "default", "bin"));
    addUniqueDirectory(directories, join(home, ".local", "share", "fnm", "current", "bin"));
    for (const nvmBinDir of resolveNvmDefaultBinDirectories(nvmDir)) {
        addUniqueDirectory(directories, nvmBinDir);
    }
    const npmConfigPrefix = normalizeOptionalText(process.env.NPM_CONFIG_PREFIX);
    if (npmConfigPrefix) {
        addUniqueDirectory(directories, join(npmConfigPrefix, "bin"));
    }
    return directories;
}
function addUniqueDirectory(directories, value) {
    const directory = normalizeOptionalText(value);
    if (directory && !directories.includes(directory)) {
        directories.push(directory);
    }
}
async function resolveExecutablePathsFromLoginShell(commandName) {
    const normalizedCommandName = normalizeOptionalText(commandName);
    if (!(normalizedCommandName && process.platform !== "win32")) {
        return [];
    }
    const shell = normalizeOptionalText(process.env.SHELL);
    if (!(shell && isAbsolute(shell) && pathLooksExecutable(shell))) {
        return [];
    }
    const cacheKey = [
        shell,
        normalizedCommandName,
        process.env.HOME ?? "",
        process.env.PATH ?? "",
    ].join("\0");
    let cached = loginShellExecutablePathsCache.get(cacheKey);
    if (!cached) {
        cached = runLoginShellExecutableLookup({
            commandName: normalizedCommandName,
            shell,
        });
        loginShellExecutablePathsCache.set(cacheKey, cached);
    }
    return await cached;
}
async function runLoginShellExecutableLookup(input) {
    const timeoutMs = resolveLoginShellLookupTimeoutMs();
    return await new Promise((resolve) => {
        const child = spawn(input.shell, ["-lic", `command -v ${quotePosixShellToken(input.commandName)}`], {
            env: process.env,
            stdio: ["ignore", "pipe", "ignore"],
            windowsHide: true,
        });
        let stdout = "";
        let settled = false;
        const finish = (paths) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timeout);
            resolve(paths);
        };
        const timeout = setTimeout(() => {
            child.kill();
            finish([]);
        }, timeoutMs);
        child.stdout?.on("data", (chunk) => {
            stdout += chunk.toString();
        });
        child.once("error", () => {
            finish([]);
        });
        child.once("close", (code) => {
            if (code !== 0) {
                finish([]);
                return;
            }
            finish(stdout
                .split(LOGIN_SHELL_OUTPUT_LINE_SPLIT_RE)
                .map((line) => normalizeOptionalText(line))
                .filter((line) => Boolean(line && isAbsolute(line) && pathLooksExecutable(line))));
        });
    });
}
function resolveNvmDefaultBinDirectories(nvmDir) {
    const version = resolveNvmDefaultVersion(nvmDir);
    if (!version) {
        return [];
    }
    return [join(nvmDir, "versions", "node", version, "bin")];
}
function resolveNvmDefaultVersion(nvmDir) {
    let value = readNvmAlias(nvmDir, "default");
    const seenAliases = new Set();
    for (let depth = 0; depth < 4; depth += 1) {
        const normalizedValue = normalizeOptionalText(value);
        if (!normalizedValue) {
            return null;
        }
        if (isNvmVersionValue(normalizedValue)) {
            return normalizedValue.startsWith("v")
                ? normalizedValue
                : `v${normalizedValue}`;
        }
        if (seenAliases.has(normalizedValue)) {
            return null;
        }
        seenAliases.add(normalizedValue);
        value = readNvmAlias(nvmDir, normalizedValue);
    }
    return null;
}
function readNvmAlias(nvmDir, alias) {
    try {
        return normalizeOptionalText(readFileSync(join(nvmDir, "alias", alias), "utf8"));
    }
    catch {
        return null;
    }
}
function isNvmVersionValue(value) {
    return NVM_VERSION_VALUE_RE.test(value);
}
function resolveHomeDirectory() {
    return normalizeOptionalText(process.env.HOME) ?? homedir();
}
function resolveLoginShellLookupTimeoutMs() {
    const raw = normalizeOptionalText(process.env.OPENMELD_EXECUTABLE_LOGIN_SHELL_TIMEOUT_MS);
    if (!raw) {
        return DEFAULT_LOGIN_SHELL_LOOKUP_TIMEOUT_MS;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0
        ? parsed
        : DEFAULT_LOGIN_SHELL_LOOKUP_TIMEOUT_MS;
}
function mergeUniquePaths(left, right) {
    const merged = [...left];
    for (const path of right) {
        if (!merged.includes(path)) {
            merged.push(path);
        }
    }
    return merged;
}
function quotePosixShellToken(value) {
    if (SAFE_POSIX_SHELL_TOKEN_RE.test(value)) {
        return value;
    }
    return `'${value.replaceAll("'", "'\\''")}'`;
}
export function resolveSiblingCommandPath(input) {
    const normalizedCommandName = normalizeOptionalText(input.commandName);
    if (!normalizedCommandName) {
        return null;
    }
    const siblingDirectory = dirname(input.anchorPath);
    for (const candidateName of resolveCommandFileNameCandidates(normalizedCommandName)) {
        const candidate = join(siblingDirectory, candidateName);
        if (pathLooksExecutable(candidate)) {
            return candidate;
        }
    }
    return null;
}
async function resolvePinnedNodePath(input) {
    const firstLine = normalizeOptionalText(input.firstLine);
    if (!firstLine) {
        return null;
    }
    if (firstLine === ENV_NODE_SHEBANG) {
        const nodeSiblings = [
            join(dirname(input.commandPath), "node"),
            join(dirname(input.scriptPath), "node"),
        ].filter((candidate, index, candidates) => {
            return candidates.indexOf(candidate) === index;
        });
        for (const nodeSibling of nodeSiblings) {
            if (await canAccessExecutablePath(nodeSibling)) {
                return nodeSibling;
            }
        }
        return null;
    }
    if (!firstLine.startsWith("#!")) {
        return null;
    }
    const interpreter = normalizeOptionalText(firstLine.slice(2));
    if (!(interpreter && isAbsolute(interpreter))) {
        return null;
    }
    const normalizedInterpreter = interpreter.split(SHEBANG_ARGS_SPLIT_RE, 1)[0] ?? interpreter;
    try {
        await assertExecutablePath(normalizedInterpreter);
        return normalizedInterpreter;
    }
    catch {
        return null;
    }
}
async function assertExecutablePath(path) {
    await access(path, resolveRunnablePathAccessMode());
}
async function canAccessExecutablePath(path) {
    try {
        await assertExecutablePath(path);
        return true;
    }
    catch {
        return false;
    }
}
async function assertReadablePath(path) {
    await access(path, constants.R_OK);
}
function pathLooksExecutable(path) {
    try {
        accessSync(path, resolveRunnablePathAccessMode());
        return true;
    }
    catch {
        return false;
    }
}
function commandPathRequiresShell(commandPath) {
    if (process.platform !== "win32") {
        return false;
    }
    const normalizedExtension = extname(commandPath).toLowerCase();
    return normalizedExtension === ".cmd" || normalizedExtension === ".bat";
}
function resolveCommandFileNameCandidates(commandName) {
    if (process.platform !== "win32") {
        return [commandName];
    }
    const normalizedExtension = extname(commandName).toLowerCase();
    if (normalizedExtension.length > 0) {
        return [commandName];
    }
    return [
        ...resolveWindowsPathExtensions(process.env.PATHEXT).map((extension) => `${commandName}${extension}`),
        commandName,
    ].filter((candidate, index, candidates) => {
        return candidates.indexOf(candidate) === index;
    });
}
function resolveRunnablePathAccessMode() {
    return process.platform === "win32" ? constants.F_OK : constants.X_OK;
}
function resolveWindowsPathExtensions(value) {
    const normalized = normalizeOptionalText(value);
    if (!normalized) {
        return [...WINDOWS_PATHEXT];
    }
    return normalized
        .split(";")
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry, index, values) => entry && values.indexOf(entry) === index)
        .map((entry) => (entry.startsWith(".") ? entry : `.${entry}`));
}
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
function buildLaunchCandidate(input) {
    const installId = buildLaunchCandidateInstallId(input.launchContract);
    const sourceLabel = input.transientWrapper
        ? "Temporary wrapper"
        : resolveLaunchCandidateSourceLabel(input.commandPath);
    const detail = resolveLaunchCandidateDetail({
        commandPath: input.commandPath,
        launchContract: input.launchContract,
        transientWrapper: input.transientWrapper,
    });
    return {
        installId,
        label: sourceLabel,
        detail,
        commandPath: input.commandPath,
        launchContract: input.launchContract,
    };
}
function buildLaunchCandidateInstallId(launchContract) {
    const canonical = JSON.stringify(canonicalizeLaunchContract(launchContract));
    const digest = createHash("sha256").update(canonical).digest("hex");
    return `install_${digest.slice(0, 12)}`;
}
function canonicalizeLaunchContract(launchContract) {
    if (launchContract.kind === "executable") {
        return {
            kind: launchContract.kind,
            commandPath: launchContract.commandPath,
            argsPrefix: [...(launchContract.argsPrefix ?? [])],
        };
    }
    return {
        kind: launchContract.kind,
        nodePath: launchContract.nodePath,
        scriptPath: launchContract.scriptPath,
        argsPrefix: [...(launchContract.argsPrefix ?? [])],
    };
}
function finalizeLaunchCandidateDisplayMetadata(candidates) {
    const candidateIndexesByLabel = new Map();
    for (const [index, candidate] of candidates.entries()) {
        const currentIndexes = candidateIndexesByLabel.get(candidate.label) ?? [];
        currentIndexes.push(index);
        candidateIndexesByLabel.set(candidate.label, currentIndexes);
    }
    const nextCandidates = candidates.map((candidate) => ({ ...candidate }));
    for (const indexes of candidateIndexesByLabel.values()) {
        if (indexes.length < 2) {
            continue;
        }
        const duplicateCandidates = indexes
            .map((index) => nextCandidates[index])
            .filter((candidate) => Boolean(candidate));
        const labelSuffixes = resolveDistinctLaunchCandidateLabelSuffixes(duplicateCandidates);
        for (const [offset, index] of indexes.entries()) {
            const suffix = labelSuffixes[offset];
            if (!suffix) {
                continue;
            }
            const currentCandidate = nextCandidates[index];
            if (!currentCandidate) {
                continue;
            }
            currentCandidate.label = `${currentCandidate.label} (${suffix})`;
        }
    }
    return nextCandidates;
}
function resolveDistinctLaunchCandidateLabelSuffixes(candidates) {
    const directoryLabels = candidates.map((candidate) => resolveLaunchCandidateDirectoryLabel(candidate.commandPath));
    if (hasOnlyDistinctValues(directoryLabels)) {
        return directoryLabels;
    }
    const commandLabels = candidates.map((candidate) => shortenPathForDisplay(candidate.commandPath));
    if (hasOnlyDistinctValues(commandLabels)) {
        return commandLabels;
    }
    return candidates.map((candidate) => candidate.installId);
}
function hasOnlyDistinctValues(values) {
    return values.length === new Set(values).size;
}
function resolveLaunchCandidateSourceLabel(commandPath) {
    const normalizedPath = commandPath.toLowerCase();
    if (normalizedPath.includes(".app/contents/")) {
        return "App install";
    }
    if (normalizedPath.includes("/.nvm/")) {
        return "nvm install";
    }
    if (normalizedPath.includes("/opt/homebrew/") ||
        normalizedPath.includes("/cellar/") ||
        normalizedPath.includes("/homebrew/")) {
        return "Homebrew install";
    }
    if (normalizedPath.includes("/.asdf/")) {
        return "asdf install";
    }
    if (normalizedPath.includes("/.volta/")) {
        return "Volta install";
    }
    if (normalizedPath.includes("/.fnm/")) {
        return "fnm install";
    }
    if (normalizedPath.includes("/.local/bin/")) {
        return "User install";
    }
    if (normalizedPath.includes("/usr/local/bin/")) {
        return "System-wide install";
    }
    if (normalizedPath.startsWith("/usr/bin/") ||
        normalizedPath.startsWith("/bin/")) {
        return "System install";
    }
    return "Other install";
}
function resolveLaunchCandidateDetail(input) {
    const nodeVersion = input.launchContract.kind === "node_script"
        ? resolveNodeVersionFromPath(input.launchContract.nodePath)
        : null;
    const commandDirectory = resolveLaunchCandidateDirectoryLabel(input.commandPath);
    if (input.transientWrapper) {
        return `Temporary PATH wrapper · ${commandDirectory}`;
    }
    if (nodeVersion) {
        return `Node ${nodeVersion} · ${commandDirectory}`;
    }
    return commandDirectory;
}
function resolveLaunchCandidateDirectoryLabel(commandPath) {
    return shortenPathForDisplay(dirname(commandPath));
}
function shortenPathForDisplay(path) {
    if (!(HOME_DIR && path.startsWith(HOME_DIR))) {
        return path;
    }
    if (path === HOME_DIR) {
        return "~";
    }
    return `~${path.slice(HOME_DIR.length)}`;
}
function resolveNodeVersionFromPath(path) {
    const match = path.match(NODE_VERSION_PATH_RE);
    return normalizeOptionalText(match?.[1]);
}
async function readLaunchScriptInspection(path) {
    const file = await open(path, "r");
    let contents = "";
    try {
        const buffer = Buffer.allocUnsafe(MAX_LAUNCH_SCRIPT_INSPECTION_BYTES);
        const { bytesRead } = await file.read(buffer, 0, MAX_LAUNCH_SCRIPT_INSPECTION_BYTES, 0);
        contents = buffer.toString("utf8", 0, bytesRead);
    }
    finally {
        await file.close();
    }
    const firstLine = contents.split(FIRST_LINE_SPLIT_RE, 1)[0] ?? "";
    return {
        contents,
        firstLine: normalizeOptionalText(firstLine),
    };
}
function isTransientPathWrapper(input) {
    if (!(isInsideTemporaryDirectory(input.commandPath) ||
        isInsideTemporaryDirectory(input.scriptPath))) {
        return false;
    }
    return (hasWrapperPathSegment(input.commandPath) ||
        hasWrapperPathSegment(input.scriptPath) ||
        shellScriptLooksLikeWrapper(input));
}
function isInsideTemporaryDirectory(path) {
    const normalizedPath = normalizePathForDetection(path);
    const normalizedTempDir = TEMP_DIR
        ? normalizePathForDetection(TEMP_DIR)
        : null;
    if (normalizedTempDir &&
        pathIsEqualOrDescendant(normalizedPath, normalizedTempDir)) {
        return true;
    }
    return (pathIsEqualOrDescendant(normalizedPath, "/tmp") ||
        pathIsEqualOrDescendant(normalizedPath, "/private/tmp") ||
        MACOS_TEMP_FOLDER_RE.test(normalizedPath));
}
function hasWrapperPathSegment(path) {
    return normalizePathForDetection(path)
        .split("/")
        .filter((segment) => segment.length > 0)
        .slice(-4)
        .some((segment) => WRAPPER_PATH_SEGMENT_RE.test(segment));
}
function shellScriptLooksLikeWrapper(input) {
    const firstLine = normalizeOptionalText(input.firstLine)?.toLowerCase() ?? "";
    if (!(firstLine.includes("bash") ||
        firstLine.includes("/sh") ||
        firstLine.includes("zsh"))) {
        return false;
    }
    const contents = input.contents.toLowerCase();
    return (contents.includes("wrapper_shim") ||
        contents.includes("shim_root") ||
        (contents.includes("\nexec ") &&
            (contents.includes("wrapper") || contents.includes("shim"))));
}
function normalizePathForDetection(path) {
    return path.replaceAll("\\", "/").replace(/\/+/gu, "/").toLowerCase();
}
function pathIsEqualOrDescendant(path, parentPath) {
    return path === parentPath || path.startsWith(`${parentPath}/`);
}
function toErrorMessage(error) {
    return error instanceof Error ? error.message : String(error);
}
